import React, { useMemo } from "react";
import { Box, Typography, styled } from "@mui/material";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import type { DashboardHistoricPenaltiesSummary } from "@services/facilityDashboard";
import { DashboardSectionHeader } from "./DashboardSectionHeader";
import {
  DASHBOARD,
  getDashboardWhiteCardStyles,
} from "./dashboardTheme";
import PenaltyTrendLineChart from "./PenaltyTrendLineChart";

const SectionCard = styled(Box)(({ theme }) => getDashboardWhiteCardStyles(theme));

const SummaryMetricCard = styled(Box)({
  flex: "1 1 140px",
  minWidth: 140,
  border: `1px solid ${DASHBOARD.border}`,
  borderRadius: "10px",
  backgroundColor: DASHBOARD.tintedCardBg,
  padding: "16px 20px",
  boxSizing: "border-box",
});

function formatUsd(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

function SummaryMetric({
  label,
  value,
  sublabel,
}: {
  label: string;
  value: string;
  sublabel: string;
}) {
  return (
    <SummaryMetricCard>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: DASHBOARD.header, mb: 0.75 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 28,
          fontWeight: 700,
          color: DASHBOARD.metricAccent,
          lineHeight: 1.1,
          letterSpacing: "-0.02em",
        }}
      >
        {value}
      </Typography>
      <Typography sx={{ fontSize: 12, color: DASHBOARD.text, mt: 0.75 }}>{sublabel}</Typography>
    </SummaryMetricCard>
  );
}

export default function HistoricPenaltiesDashboardCard({
  title,
  subtitle,
  penalties,
  emptyMessage,
  onViewHistory,
  infoTooltip,
}: {
  title: string;
  subtitle: string;
  penalties: DashboardHistoricPenaltiesSummary;
  emptyMessage: string;
  onViewHistory?: () => void;
  infoTooltip?: React.ReactNode;
}) {
  const trendPoints = useMemo(
    () =>
      [...penalties.yearly_totals]
        .sort((a, b) => a.year - b.year)
        .map((row) => ({ year: row.year, amount: row.amount })),
    [penalties.yearly_totals],
  );

  const yearCount = trendPoints.length > 0 ? trendPoints.length : 5;

  return (
    <SectionCard>
      <DashboardSectionHeader
        icon={<AssessmentOutlinedIcon fontSize="small" />}
        title={title}
        subtitle={subtitle}
        onAction={onViewHistory}
        actionLabel="View full history →"
        infoTooltip={infoTooltip}
      />

      {!penalties.has_data ? (
        <Typography sx={{ fontSize: 14, color: DASHBOARD.text, mt: 2, py: 1 }}>
          {emptyMessage}
        </Typography>
      ) : (
        <Box sx={{ mt: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mb: 3,
            }}
          >
            <SummaryMetric
              label="Total penalties"
              value={formatUsd(penalties.total_amount)}
              sublabel={`across ${yearCount} years`}
            />
            <SummaryMetric
              label="Violations"
              value={String(penalties.total_violations)}
              sublabel="total citations"
            />
            <SummaryMetric
              label="Most cited"
              value={penalties.most_cited_tag ?? "—"}
              sublabel={
                penalties.most_cited_tag
                  ? `${penalties.most_cited_count} citation${penalties.most_cited_count === 1 ? "" : "s"}`
                  : "no tag data"
              }
            />
          </Box>

          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 700,
              color: DASHBOARD.header,
              mb: 1.5,
            }}
          >
            Penalty trend
          </Typography>

          <PenaltyTrendLineChart points={trendPoints} />
        </Box>
      )}
    </SectionCard>
  );
}
